//frontend/src/components/Dashboard/AdminDashboard.jsx

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { FaUsers, FaFileAlt, FaClock, FaCheckCircle, FaChartLine } from 'react-icons/fa';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalDossiers: 0,
        pendingDossiers: 0,
        approvedDossiers: 0,
        rejectedDossiers: 0
    });
    const [recentDossiers, setRecentDossiers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            const usersResponse = await api.get('/admin/users');
            const users = usersResponse.data.users.data || [];

            const dossiersResponse = await api.get('/admin/dossiers');
            const dossiers = dossiersResponse.data.dossiers.data || [];

            console.log('Users:', users);
            console.log('Dossiers:', dossiers);

            const pending = dossiers.filter(d => d.status === 'submitted' || d.status === 'in_review').length;
            const approved = dossiers.filter(d => d.status === 'approved').length;
            const rejected = dossiers.filter(d => d.status === 'rejected').length;

            setStats({
                totalUsers: users.length,
                totalDossiers: dossiers.length,
                pendingDossiers: pending,
                approvedDossiers: approved,
                rejectedDossiers: rejected
            });

            const recent = dossiers
                .filter(d => d.status !== 'draft')
                .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
                .slice(0, 5);

            setRecentDossiers(recent);

        } catch (err) {
            console.error('Error loading dashboard data:', err);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            draft: { variant: 'secondary', text: 'Brouillon' },
            submitted: { variant: 'info', text: 'Soumis' },
            in_review: { variant: 'warning', text: 'En révision' },
            approved: { variant: 'success', text: 'Approuvé' },
            rejected: { variant: 'danger', text: 'Rejeté' }
        };
        const config = statusConfig[status] || { variant: 'secondary', text: status };
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const getVisaTypeLabel = (visaType) => {
        const types = {
            work: 'Travail',
            study: 'Études',
            tourist: 'Tourisme',
            family: 'Famille',
            business: 'Affaires'
        };
        return types[visaType] || visaType;
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Chargement du tableau de bord...</p>
            </Container>
        );
    }

    return (
        <Container fluid className="py-4">
            <h2 className="mb-4">
                <FaChartLine className="me-2" />
                Tableau de bord Admin
            </h2>

            <Row className="mb-4">
                <Col md={6} lg={3} className="mb-3">
                    <Card className="shadow-sm border-0 stat-card">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="text-muted mb-1">Total Utilisateurs</p>
                                    <h3 className="mb-0">{stats.totalUsers}</h3>
                                </div>
                                <div className="stat-icon-wrapper bg-primary bg-opacity-10">
                                    <FaUsers size={30} className="text-primary" />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6} lg={3} className="mb-3">
                    <Card className="shadow-sm border-0 stat-card">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="text-muted mb-1">Total Dossiers</p>
                                    <h3 className="mb-0">{stats.totalDossiers}</h3>
                                </div>
                                <div className="stat-icon-wrapper bg-info bg-opacity-10">
                                    <FaFileAlt size={30} className="text-info" />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6} lg={3} className="mb-3">
                    <Card className="shadow-sm border-0 stat-card">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="text-muted mb-1">En Attente</p>
                                    <h3 className="mb-0">{stats.pendingDossiers}</h3>
                                </div>
                                <div className="stat-icon-wrapper bg-warning bg-opacity-10">
                                    <FaClock size={30} className="text-warning" />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6} lg={3} className="mb-3">
                    <Card className="shadow-sm border-0 stat-card">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="text-muted mb-1">Approuvés</p>
                                    <h3 className="mb-0">{stats.approvedDossiers}</h3>
                                </div>
                                <div className="stat-icon-wrapper bg-success bg-opacity-10">
                                    <FaCheckCircle size={30} className="text-success" />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col lg={12}>
                    <Card className="shadow-sm border-0">
                        <Card.Header className="bg-white py-3">
                            <h5 className="mb-0">Dossiers récents</h5>
                        </Card.Header>
                        <Card.Body>
                            {recentDossiers.length === 0 ? (
                                <Alert variant="info">
                                    Aucun dossier soumis pour le moment.
                                </Alert>
                            ) : (
                                <Table responsive hover>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Utilisateur</th>
                                            <th>Type de Visa</th>
                                            <th>Statut</th>
                                            <th>Date de soumission</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentDossiers.map((dossier) => (
                                            <tr key={dossier.id}>
                                                <td>{dossier.id}</td>
                                                <td>{dossier.user ? `${dossier.user.first_name} ${dossier.user.last_name}` : 'N/A'}</td>
                                                <td>{getVisaTypeLabel(dossier.visa_type)}</td>
                                                <td>{getStatusBadge(dossier.status)}</td>
                                                <td>{new Date(dossier.updated_at).toLocaleDateString('fr-FR')}</td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-primary"
                                                        onClick={() => navigate(`/admin/dossier/${dossier.id}`)}
                                                    >
                                                        Voir détails
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default AdminDashboard;